/**
 * Role-based permissions for eScribe
 *
 * Roles hierarchy (within an organisation):
 * - owner: Full organisation access, can delete org, manage billing
 * - admin: Can manage team members, view billing, manage settings
 * - member: Basic access to documents, templates, folders
 *
 * Special flag:
 * - isOwner: App owner (OWNER_EMAIL env var) - has access to admin dashboard
 */

// Define which pages each role can access
export const PAGE_PERMISSIONS = {
  // Main navigation - all authenticated users
  "/dashboard": ["member", "admin", "owner"],
  "/envelopes": ["member", "admin", "owner"],
  "/templates": ["member", "admin", "owner"],
  "/folders": ["member", "admin", "owner"],
  "/tags": ["member", "admin", "owner"],
  "/settings": ["member", "admin", "owner"],

  // Tools - admin and owner only
  "/merge-keys": ["admin", "owner"],
  "/api-keys": ["admin", "owner"],
  "/webhooks": ["admin", "owner"],
  "/integrations": ["admin", "owner"],
  "/embed": ["admin", "owner"],

  // Team & Account - role-specific
  "/team": ["member", "admin", "owner"], // All can view, but actions differ
  "/usage": ["admin", "owner"],
  "/billing": ["owner"], // Only owners can manage billing
  "/settings/billing": ["owner"], // Only owners can manage billing

  // Admin pages - app owner only (checked via isOwner flag)
  "/admin": ["appOwner"],
  "/admin/users": ["appOwner"],

  // Document generation
  "/document-generation": ["admin", "owner"],
};

// Define which features each role can use
export const FEATURE_PERMISSIONS = {
  // Document actions
  createEnvelope: ["member", "admin", "owner"],
  editEnvelope: ["member", "admin", "owner"],
  deleteEnvelope: ["member", "admin", "owner"],
  voidEnvelope: ["admin", "owner"],

  // Template actions
  createTemplate: ["member", "admin", "owner"],
  editTemplate: ["member", "admin", "owner"],
  deleteTemplate: ["admin", "owner"],

  // Team actions
  viewTeam: ["member", "admin", "owner"],
  inviteMembers: ["admin", "owner"],
  removeMembers: ["admin", "owner"],
  changeRoles: ["owner"],

  // Organisation actions
  editOrganization: ["admin", "owner"],
  deleteOrganization: ["owner"],

  // Billing actions
  viewBilling: ["owner"],
  manageBilling: ["owner"],

  // API & Developer
  viewApiKeys: ["admin", "owner"],
  createApiKeys: ["admin", "owner"],
  deleteApiKeys: ["owner"],
  viewEmbed: ["admin", "owner"],
  manageEmbed: ["owner"],

  // Admin actions (app owner only)
  viewAdminDashboard: ["appOwner"],
  manageUsers: ["appOwner"],
  enableFeatures: ["appOwner"],
};

/**
 * Check if user has access to a specific page
 * @param {Object} user - User object with teamRole and isOwner
 * @param {string} path - Page path to check
 * @returns {boolean}
 */
export function canAccessPage(user, path) {
  if (!user) return false;

  // App owner can access everything
  if (user.isOwner) return true;

  // Find matching permission
  const permission = PAGE_PERMISSIONS[path];
  if (!permission) return true; // If not defined, allow access

  // Check for appOwner-only pages
  if (permission.includes("appOwner") && !user.isOwner) {
    return false;
  }

  // Check team role
  // Default to 'owner' if user has org but no teamRole (they created the org)
  const hasOrg = user.organisationId || user.organisation;
  const teamRole = user.teamRole || (hasOrg ? "owner" : "member");
  return permission.includes(teamRole);
}

/**
 * Check if user has a specific feature permission
 * @param {Object} user - User object with teamRole and isOwner
 * @param {string} feature - Feature key to check
 * @returns {boolean}
 */
export function canUseFeature(user, feature) {
  if (!user) return false;

  // App owner can do everything
  if (user.isOwner) return true;

  const permission = FEATURE_PERMISSIONS[feature];
  if (!permission) return true; // If not defined, allow

  // Check for appOwner-only features
  if (permission.includes("appOwner") && !user.isOwner) {
    return false;
  }

  // Default to 'owner' if user has org but no teamRole (they created the org)
  // Otherwise default to 'member'
  const hasOrg = user.organisationId || user.organisation;
  const teamRole = user.teamRole || (hasOrg ? "owner" : "member");
  return permission.includes(teamRole);
}

/**
 * Get filtered navigation items based on user permissions
 * @param {Array} items - Navigation items array
 * @param {Object} user - User object
 * @returns {Array} Filtered items
 */
export function filterNavItems(items, user) {
  if (!user) return [];
  return items.filter((item) => canAccessPage(user, item.href));
}

/**
 * Check if user is organisation owner
 * A user is considered an org owner if:
 * 1. Their teamRole is explicitly 'owner', OR
 * 2. They have an organisationId but no teamRole (legacy/edge case - they created the org)
 */
export function isOrgOwner(user) {
  if (user?.teamRole === "owner") return true;
  // If user has org but no teamRole, they're likely the owner (self-created org)
  if ((user?.organisationId || user?.organisation) && !user?.teamRole) {
    return true;
  }
  return false;
}

/**
 * Check if user is organisation admin or owner
 * Includes fallback for users with org but no teamRole set
 */
export function isOrgAdmin(user) {
  if (["admin", "owner"].includes(user?.teamRole)) return true;
  // If user has org but no teamRole, they're likely the owner (self-created org)
  if ((user?.organisationId || user?.organisation) && !user?.teamRole) {
    return true;
  }
  return false;
}

/**
 * Check if user is the app owner (superadmin)
 */
export function isAppOwner(user) {
  return user?.isOwner === true;
}

/**
 * Get user's display role
 */
export function getUserRoleDisplay(user) {
  if (!user) return "User";
  if (user.isOwner) return "App Owner";

  // If no organisation, they're just a user
  if (!user.organisationId && !user.organisation) {
    return "User";
  }

  // Return team role display
  switch (user.teamRole) {
    case "owner":
      return "Owner";
    case "admin":
      return "Admin";
    case "member":
      return "Member";
    case "viewer":
      return "Viewer";
    default:
      return "Owner"; // Default to Owner if in org but role unknown (they created it)
  }
}

export default {
  PAGE_PERMISSIONS,
  FEATURE_PERMISSIONS,
  canAccessPage,
  canUseFeature,
  filterNavItems,
  isOrgOwner,
  isOrgAdmin,
  isAppOwner,
  getUserRoleDisplay,
};
